import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const SettlementPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [zoneData, setZoneData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkSettlementZone = async () => {
      try {
        const data = await api.checkZone(token);
        setZoneData(data);
      } catch (err) {
        setError(err.message || 'Failed to check settlement zone');
      } finally {
        setLoading(false);
      }
    };

    checkSettlementZone();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing settlement possibilities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const hasSettlementZone = zoneData?.zoneExists;
  const quality = zoneData?.quality;
  const message = zoneData?.message;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressBar current={4} total={4} phase="Settlement Review" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Settlement Analysis Complete</h1>
        <p className="text-gray-600">
          Our AI has analyzed both positions to find potential settlement options
        </p>
      </div>

      {hasSettlementZone ? (
        <>
          {/* Success - Settlement Zone Found */}
          <div className={`card border-2 mb-6 ${
            quality === 'strong' ? 'bg-green-50 border-green-300' :
            quality === 'moderate' ? 'bg-blue-50 border-blue-300' :
            'bg-yellow-50 border-yellow-300'
          }`}>
            <div className="text-center">
              <div className="text-6xl mb-4">
                {quality === 'strong' ? '✅' : quality === 'moderate' ? '👍' : '🤝'}
              </div>
              <h2 className={`text-2xl font-bold mb-3 ${
                quality === 'strong' ? 'text-green-800' :
                quality === 'moderate' ? 'text-blue-800' :
                'text-yellow-800'
              }`}>
                Settlement Zone Found!
              </h2>
              <p className="text-gray-700 mb-2">
                {message || 'There is overlap between the two positions. A settlement is possible.'}
              </p>
              <div className="mt-4 inline-block px-4 py-2 bg-white rounded-lg">
                <p className="text-sm text-gray-600">Zone Quality</p>
                <p className="font-bold text-lg capitalize">{quality}</p>
              </div>
            </div>
          </div>

          {/* Settlement Details */}
          <div className="card mb-6">
            <h3 className="text-xl font-bold mb-4">Analysis Complete</h3>

            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">
                <strong>Good news!</strong> Our analysis shows that there is common ground between both parties' positions.
              </p>
            </div>

            {zoneData.timelineCompatible && (
              <div className="flex items-center mb-3 text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Payment timelines are compatible</span>
              </div>
            )}

            {zoneData.relationshipCompatible && (
              <div className="flex items-center mb-3 text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Relationship considerations align</span>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="card bg-blue-50 border-blue-200 mb-6">
            <h3 className="font-bold mb-3">Next Steps</h3>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">1.</span>
                <span>Review the proposed settlement terms carefully</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">2.</span>
                <span>An attorney will review and approve the settlement agreement</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">3.</span>
                <span>Both parties will be notified to review and sign the agreement</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">4.</span>
                <span>Once signed, payment instructions will be provided</span>
              </li>
            </ol>
          </div>

          <div className="card bg-yellow-50 border-yellow-300">
            <div className="flex items-start">
              <div className="text-2xl mr-3">📧</div>
              <div>
                <h4 className="font-bold mb-2">What Happens Now?</h4>
                <p className="text-sm text-gray-700">
                  We'll notify you by email within 24 hours once the attorney has reviewed this settlement.
                  You'll receive a link to review and sign the final agreement. No payment is required until
                  both parties have signed.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* No Settlement Zone */}
          <div className="card bg-orange-50 border-2 border-orange-300 mb-6">
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-orange-800 mb-3">
                No Settlement Zone Found
              </h2>
              <p className="text-gray-700 mb-4">
                {message || 'Unfortunately, there is no overlap between the two positions at this time.'}
              </p>
            </div>
          </div>

          {/* Analysis Info */}
          <div className="card mb-6 bg-gray-50">
            <h3 className="text-lg font-bold mb-3">Analysis Results</h3>
            <p className="text-sm text-gray-700 mb-4">
              Our AI has analyzed both parties' positions and found that the acceptable ranges
              do not currently overlap. This doesn't mean settlement is impossible - it may require
              adjustment or creative solutions.
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className={`mr-2 ${zoneData.timelineCompatible ? 'text-green-500' : 'text-red-500'}`}>
                  {zoneData.timelineCompatible ? '✓' : '✗'}
                </span>
                <span className="text-gray-700">
                  Payment timeline {zoneData.timelineCompatible ? 'compatible' : 'needs adjustment'}
                </span>
              </div>
              <div className="flex items-center">
                <span className={`mr-2 ${zoneData.relationshipCompatible ? 'text-green-500' : 'text-red-500'}`}>
                  {zoneData.relationshipCompatible ? '✓' : '✗'}
                </span>
                <span className="text-gray-700">
                  Relationship considerations {zoneData.relationshipCompatible ? 'align' : 'differ'}
                </span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="card bg-blue-50 border-blue-200 mb-6">
            <h3 className="font-bold mb-3">Your Options</h3>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-medium mb-1">1. Wait for the Other Party</h4>
                <p className="text-gray-600">
                  The other party may adjust their position. We'll notify you if a settlement zone emerges.
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-medium mb-1">2. Revise Your Parameters</h4>
                <p className="text-gray-600">
                  You can go back and adjust your settlement parameters if you'd like to increase your range.
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-medium mb-1">3. Explore Alternative Resolution</h4>
                <p className="text-gray-600">
                  An attorney may be able to suggest alternative terms or mediation approaches.
                </p>
              </div>
            </div>
          </div>

          <div className="card bg-yellow-50 border-yellow-300">
            <div className="flex items-start">
              <div className="text-2xl mr-3">💼</div>
              <div>
                <h4 className="font-bold mb-2">Attorney Review</h4>
                <p className="text-sm text-gray-700">
                  An attorney will still review this case and may contact both parties to explore
                  other resolution options. You'll receive an email update within 48 hours.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={() => navigate(`/respond/${token}/parameters`)}
              className="btn-secondary"
            >
              Revise Parameters
            </button>
            <button
              type="button"
              onClick={() => navigate(`/respond/${token}`)}
              className="btn-primary"
            >
              Wait for Update
            </button>
          </div>
        </>
      )}

      {/* Help Section */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 mb-2">
          Questions about this process?
        </p>
        <p className="text-sm text-gray-500">
          Contact support@tensionbridge.com or review our{' '}
          <a href="https://tensionbridge.pages.dev/faq" className="text-primary-500 hover:underline" target="_blank" rel="noopener noreferrer">
            FAQ
          </a>
        </p>
      </div>

      <div className="mt-6 text-center text-xs text-gray-400">
        <p>
          🔒 All information remains confidential • ⚖️ Attorney-supervised process
        </p>
      </div>
    </div>
  );
};

export default SettlementPage;
